import { useDictionary } from 'src/hooks/useDictionary';

import AccordionSection from 'components/BaseHelpers/AccordionSection';

import { ComponentProps } from '../../../types/component-props';
import { CoreCredentialsData } from '../../../types/components';

import styles from './CoreCredentials.module.scss';

export type CoreCredentialsProps = ComponentProps & CoreCredentialsData;

const CoreCredentials = (props: CoreCredentialsProps): JSX.Element => {
  const fields = props.fields;
  const { header, jumpLinkLabel, description, auxiliaryItems } = fields;
  const dictionary = useDictionary();

  return (
    <AccordionSection
      heading={header}
      jumpLinkLabel={jumpLinkLabel}
      description={description}
      panels={[
        formatEducationAdmissions(fields, dictionary),
        formatLanguages(fields, dictionary),
        formatAwards(fields, dictionary),
        formatInvolvement(fields, dictionary),
        formatAcknowledgements(fields, dictionary),
        ...auxiliaryItems,
      ]}
    />
  );
};

const formatEducationAdmissions = (
  fields: CoreCredentialsProps['fields'],
  dictionary: ReturnType<typeof useDictionary>
) => {
  const educationContent = fields.education.length
    ? `
      <div class="${styles['education-label']}"><strong>${dictionary.educationLabel}</strong></div>
      ${fields.education
        .map(
          (education) => `
            <div class="${styles['education-item']}">
              <div class="${styles['education-subhead']}"><strong>${education.school}</strong></div>
              ${[education.degree, education.year].filter(Boolean).join(' | ')}
              ${education.concentration ? `<div>${education.concentration}</div>` : ''}
              ${
                education.details || education.honors
                  ? `<div>${[education.details, education.honors].filter(Boolean).join('')}</div>`
                  : ''
              }
            </div>`
        )
        .join('')}
    `
    : '';

  const barAdmissionsContent = fields.barAdmissions.length
    ? `
      <div>
        <div class="${styles['education-label']}"><strong>${dictionary.barAdmissionsLabel}</strong></div>
        ${fields.barAdmissions
          .map(
            (bar) => `
            <div class="${styles['education-item']}">
                <strong class="${styles['education-subhead']}">${[bar.state, bar.year].filter(Boolean).join(' | ')}</strong>
                ${bar.details ? `${bar.details}` : ''}
            </div>`
          )
          .join('')}
      </div>`
    : '';

  const courtAdmissionsLabel = fields.courtAdmissionsLabel || dictionary.courtAdmissionsLabel;
  const courtAdmissionsContent = fields.courtAdmissions.length
    ? `
      <div class="${styles['education-item']}">
        <div class="${styles['education-label']}"><strong>${courtAdmissionsLabel}</strong></div>
        <div>${fields.courtAdmissions}</div>
      </div>`
    : '';

  const admissionsContent = [barAdmissionsContent, courtAdmissionsContent].filter(Boolean).join('');

  const content = [
    educationContent && `<div>${educationContent}</div>`,
    admissionsContent && `<div>${admissionsContent}</div>`,
  ].join('');

  return {
    label: dictionary.educationAdmissionsLabel,
    content: content ? `<div class="${styles.grid}">${content}</div>` : '',
  };
};

const formatLanguages = (
  fields: CoreCredentialsProps['fields'],
  dictionary: ReturnType<typeof useDictionary>
) => {
  return {
    label: dictionary.languagesLabel,
    content: fields.languages.length
      ? `
      <ul class="${styles.grid}">
        ${fields.languages.map((language) => `<li>${language}</li>`).join('')}
      </ul>
    `
      : '',
  };
};

const formatAwards = (
  fields: CoreCredentialsProps['fields'],
  dictionary: ReturnType<typeof useDictionary>
) => {
  const content = [
    fields.awards.length > 0 &&
      `
      <ul class="${styles['awards-list']}">
        ${fields.awards
          .map(
            (award) => `
          <li>
            <img src="${award.image?.src}" alt="${award.image?.alt}" loading="lazy"/>
            <div>${award.description}</div>
          </li>
        `
          )
          .join('')}
      </ul>
    `,
    fields.awardsListing && `${fields.awardsListing}`,
  ]
    .filter(Boolean)
    .join('<br/>');
  return {
    label: dictionary.awardsLabel,
    content,
  };
};

const formatInvolvement = (
  fields: CoreCredentialsProps['fields'],
  dictionary: ReturnType<typeof useDictionary>
) => {
  const content = [
    fields.professionalInvolvement &&
      `<div><strong>${dictionary.professionalInvolvementLabel}</strong></div>${fields.professionalInvolvement}`,
    fields.communityInvolvement &&
      `<div><strong>${dictionary.communityInvolvementLabel}</strong></div>${fields.communityInvolvement}`,
  ]
    .filter(Boolean)
    .join('<br/>');

  return {
    label: dictionary.involvementLabel,
    content: content,
  };
};

const formatAcknowledgements = (
  fields: CoreCredentialsProps['fields'],
  dictionary: ReturnType<typeof useDictionary>
) => {
  return {
    label: dictionary.acknowledgementsLabel,
    content: fields.acknowledgements,
  };
};

export default CoreCredentials;
